/**
 * Pusher Client Service
 *
 * SINGLETON Pusher instance shared across all components.
 * Prevents multiple WebSocket connections.
 */

import Pusher from 'pusher-js';

const PUSHER_KEY = import.meta.env.VITE_PUSHER_KEY;
const PUSHER_CLUSTER = import.meta.env.VITE_PUSHER_CLUSTER;

if (!PUSHER_KEY || !PUSHER_CLUSTER) {
  console.error('Missing Pusher configuration. Please set VITE_PUSHER_KEY and VITE_PUSHER_CLUSTER in .env');
}

/**
 * SINGLETON Pusher instance - shared across all components
 */
let pusherInstance: Pusher | null = null;

/**
 * Get or create the SINGLETON Pusher instance
 * This prevents multiple WebSocket connections
 */
export function getPusherInstance(): Pusher {
  if (!pusherInstance && PUSHER_KEY && PUSHER_CLUSTER) {
    console.log('[Pusher] Creating SINGLETON instance...');
    
    // Enable debug logging
    Pusher.logToConsole = true;
    
    pusherInstance = new Pusher(PUSHER_KEY, {
      cluster: PUSHER_CLUSTER,
      enabledTransports: ['ws', 'wss'],
      forceTLS: true,
    });

    // Connection state logging
    pusherInstance.connection.bind('state_change', (states: any) => {
      console.log('[Pusher] Connection state changed from', states.previous, 'to', states.current);
    });

    pusherInstance.connection.bind('connected', () => {
      console.log('[Pusher] Connected to Pusher');
    });

    pusherInstance.connection.bind('disconnected', () => {
      console.log('[Pusher] Disconnected from Pusher');
    });

    pusherInstance.connection.bind('error', (err: any) => {
      console.error('[Pusher] Connection error:', err);
    });
  }

  return pusherInstance!;
}

/**
 * Subscribe to dashboard updates channel
 */
export function subscribeToDashboard(
  onCallStarted: (data: any) => void,
  onCallUpdated: (data: any) => void,
  onCallEnded: (data: any) => void,
  onNotificationCreated: (data: any) => void
) {
  const pusher = getPusherInstance();
  const channel = pusher.subscribe('dashboard-updates');

  console.log('[Pusher] Subscribing to dashboard-updates channel');
  
  // Wrap handlers with debug logging
  const wrappedCallStarted = (data: any) => {
    console.log('[Pusher] Received call_started event:', data);
    onCallStarted(data);
  };
  
  const wrappedCallUpdated = (data: any) => {
    console.log('[Pusher] Received call_updated event:', data);
    onCallUpdated(data);
  };
  
  const wrappedCallEnded = (data: any) => {
    console.log('[Pusher] Received call_ended event:', data);
    onCallEnded(data);
  };
  
  const wrappedNotificationCreated = (data: any) => {
    console.log('[Pusher] Received notification_created event:', data);
    onNotificationCreated(data);
  };

  channel.bind('call_started', wrappedCallStarted);
  channel.bind('call_updated', wrappedCallUpdated);
  channel.bind('call_ended', wrappedCallEnded);
  channel.bind('notification_created', wrappedNotificationCreated);
  
  // Log subscription success
  channel.bind('pusher:subscription_succeeded', () => {
    console.log('[Pusher] Successfully subscribed to dashboard-updates');
  });
  
  // Log subscription errors
  channel.bind('pusher:subscription_error', (error: any) => {
    console.error('[Pusher] Subscription error:', error);
  });

  return () => {
    channel.unbind('call_started', wrappedCallStarted);
    channel.unbind('call_updated', wrappedCallUpdated);
    channel.unbind('call_ended', wrappedCallEnded);
    channel.unbind('notification_created', wrappedNotificationCreated);
    channel.unbind('pusher:subscription_succeeded');
    channel.unbind('pusher:subscription_error');
    pusher.unsubscribe('dashboard-updates');
    console.log('[Pusher] Unsubscribed from dashboard-updates');
  };
}

/**
 * Get connection state
 */
export function getPusherConnectionState(): string {
  return pusherInstance?.connection.state || 'disconnected';
}

/**
 * Disconnect Pusher
 */
export function disconnectPusher(): void {
  if (pusherInstance) {
    pusherInstance.disconnect();
    pusherInstance = null;
  }
}